import React, { useRef, useState } from "react";
import Lucide from "src/base-components/Lucide";
import { FormTextarea } from "src/base-components/Form";
import EmojiPicker from "src/components/EmojiPicker";

interface ChatMessageInputProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onSend: () => void;
  onEditSave?: () => void;
  onCancelEdit?: () => void;
  editing?: boolean;
  /** Multiple selected files (new) */
  selectedFiles?: File[];
  /** @deprecated – kept for backward compat; prefer selectedFiles */
  selectedFile?: File | null;
  onFileSelect?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onEmojiSelect?: (emoji: string) => void;
  allowedFileTypes?: string;
  maxHeight?: string;
  className?: string;
}

const ChatMessageInput: React.FC<ChatMessageInputProps> = ({
  value,
  onChange,
  onSend,
  onEditSave,
  onCancelEdit,
  editing = false,
  selectedFiles = [],
  selectedFile,
  onFileSelect,
  onEmojiSelect,
  allowedFileTypes = "*",
  maxHeight = "120px",
  className = "",
}) => {
  // Derive hasFiles from new prop or legacy prop
  const hasFiles = selectedFiles.length > 0 || !!selectedFile;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);

  const handleSendClick = () => {
    editing ? onEditSave?.() : onSend();
  };

  return (
    <div
      className={`flex items-end gap-3 bg-white rounded-2xl px-6 py-3 shadow-md relative ${className}`}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={allowedFileTypes}
        multiple
        onChange={onFileSelect}
        onClick={(e) => { (e.target as HTMLInputElement).value = ""; }}
        className="hidden"
      />

      <button
        onClick={() => fileInputRef.current?.click()}
        className="text-purple-600 hover:text-purple-800 transition mb-[4px]"
        type="button"
      >
        <Lucide icon="Paperclip" className="w-5 h-5" />
      </button>

      <div className="relative">
        <button
          onClick={() => setIsEmojiPickerOpen((prev) => !prev)}
          className="text-purple-600 hover:text-purple-800 transition"
          title="Add emoji"
          type="button"
        >
          <Lucide icon="Smile" className="w-5 h-5" />
        </button>

        {onEmojiSelect && (
          <EmojiPicker
            isOpen={isEmojiPickerOpen}
            onClose={() => setIsEmojiPickerOpen(false)}
            onEmojiSelect={onEmojiSelect}
          />
        )}
      </div>

      <FormTextarea
        ref={textareaRef}
        rows={1}
        placeholder={editing ? "Edit your message..." : "Type a message..."}
        value={value}
        onChange={onChange}
        className="!border-0 !shadow-none !ring-0 focus:!ring-0 focus:!border-0 resize-none overflow-y-auto"
        style={{ maxHeight }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSendClick();
          }
          if (e.key === "Escape" && editing) {
            onCancelEdit?.();
          }
        }}
      />

      <button
        onClick={handleSendClick}
        disabled={!value.trim() && !hasFiles && !editing}
        className="w-11 h-9 rounded-full flex items-center justify-center shadow-lg disabled:opacity-50"
        style={{
          background: editing
            ? "linear-gradient(180deg, #3B82F6 0%, #1D4ED8 100%)"
            : "linear-gradient(180deg, rgba(76, 29, 149, 0.90) 0%, rgba(159, 18, 57, 0.90) 100%)",
        }}
        title={editing ? "Save edit" : "Send message"}
        type="button"
      >
        <Lucide
          icon={editing ? "Check" : "Send"}
          className="w-4 h-4 text-white"
        />
      </button>
    </div>
  );
};

export default ChatMessageInput;
